import {peakAlert} from "../PeakDialog.js";
import jQuery from 'jquery';
import {SavedListingRepository} from "./saved-listings/SavedListingRepository.js";
import {RangeParam, SearchParams} from "./SearchParams.js";
import { ZIDX } from "./standaloneLibrary.js";
import {
    ZIDXListingSummary, ZIDXListingData, ZIDXSearchFields, ZIDXAdvancedQueryResult,
    LISTING_LAYOUT_LIST, LISTING_LAYOUT_DETAIL, LISTING_LAYOUT_MAP
} from "./ListingModels.js";
import { displayListings } from "./ListingRenderer.js";
import type { ZIDXBase } from "./base.js";

let $=jQuery.noConflict();

/**
 * Builds a complete advancedSearchQuery string that encodes ALL search
 * criteria — enum checkboxes, numeric ranges, and partial-match strings.
 *
 * Uses the new query syntax:
 *   - price BETWEEN (300000, 500000)
 *   - beds >= 3
 *   - subdivision IN ('%Ocean%')
 *   - remarks NOT IN ('%fixer upper%')
 *
 * Called only in AI mode. In non-AI mode, the existing form-derived
 * query + top-level fields approach is used unchanged.
 */
function buildUnifiedAdvancedQuery(
    base: ZIDXBase,
    ss: ZIDXSearchFields,
    aiQuery: string
): string {
    // Start with the AI-generated query as the base
    // (contains type, subType, city, etc. from the AI)
    const parts: string[] = [];

    if (aiQuery && aiQuery.trim() !== "") {
        parts.push(aiQuery);
    }

    // ── Numeric ranges → BETWEEN / >= / <= ──

    if (ss.zidx_search_price_from != 0 || ss.zidx_search_price_to != 2000000000) {
        parts.push(buildRangeClause("price", ss.zidx_search_price_from, ss.zidx_search_price_to, 0, 2000000000));
    }
    if (ss.zidx_search_bedrooms_from != 0 || ss.zidx_search_bedrooms_to != 100) {
        parts.push(buildRangeClause("beds", ss.zidx_search_bedrooms_from, ss.zidx_search_bedrooms_to, 0, 100));
    }
    if (ss.zidx_search_bathrooms_from != 0 || ss.zidx_search_bathrooms_to != 100) {
        parts.push(buildRangeClause("baths", ss.zidx_search_bathrooms_from, ss.zidx_search_bathrooms_to, 0, 100));
    }
    if (ss.zidx_search_sqft_from != 0 || ss.zidx_search_sqft_to != 1000000000) {
        parts.push(buildRangeClause("sqft", ss.zidx_search_sqft_from, ss.zidx_search_sqft_to, 0, 1000000000));
    }
    if (ss.zidx_search_acreage_from != 0 || ss.zidx_search_acreage_to != 1000000000) {
        parts.push(buildRangeClause("acreage", ss.zidx_search_acreage_from, ss.zidx_search_acreage_to, 0, 1000000000));
    }
    if (ss.zidx_search_year_built_from != 0 || ss.zidx_search_year_built_to != 10000) {
        parts.push(buildRangeClause("yearBuilt", ss.zidx_search_year_built_from, ss.zidx_search_year_built_to, 0, 10000));
    }

    // ── Partial-match string fields → IN with % wildcards ──

    if (ss.zidx_search_subdivision != "") {
        parts.push(buildWildcardInClause("subdivision", ss.zidx_search_subdivision));
    }
    if (ss.zidx_search_address != "") {
        parts.push(buildWildcardInClause("address", ss.zidx_search_address));
    }
    if (ss.zidx_search_building != "") {
        parts.push(buildWildcardInClause("building", ss.zidx_search_building));
    }
    if (ss.zidx_search_postal_code != "") {
        parts.push(buildExactInClause("postalCode", ss.zidx_search_postal_code));
    }

    // ── Remarks → IN with % wildcards (vector search in non-AI, keyword in AI) ──
    // Note: remarks/vectorSearch is handled separately on the listingIndex entry

    // ── Exclude remarks → NOT IN with % wildcards ──
    if (ss.zidx_search_exclude_remarks != "") {
        parts.push(buildWildcardNotInClause("remarks", ss.zidx_search_exclude_remarks));
    }

    return parts.filter(p => p !== "").join(" AND ");
}


/**
 * Builds a range clause using BETWEEN, >=, or <=.
 */
function buildRangeClause(field: string, from: number, to: number, defaultFrom: number, defaultTo: number): string {
    const hasMin = from != defaultFrom;
    const hasMax = to != defaultTo;

    if (hasMin && hasMax) {
        return field + " BETWEEN (" + from + ", " + to + ")";
    } else if (hasMin) {
        return field + " >= " + from;
    } else if (hasMax) {
        return field + " <= " + to;
    }
    return "";
}


/**
 * Builds an IN clause with % wildcards for partial string matching.
 * Input: comma-separated values like "ocean,beach"
 * Output: subdivision IN ('%ocean%','%beach%')
 */
function buildWildcardInClause(field: string, csvValues: string): string {
    const values = csvValues.split(",").map(v => v.trim()).filter(v => v !== "");
    if (values.length === 0) return "";

    const quoted = values.map(v => "'%" + v.replace(/'/g, "\\'") + "%'");
    return field + " IN (" + quoted.join(",") + ")";
}


/**
 * Builds an exact IN clause (no wildcards) for fields like postalCode.
 * Input: comma-separated values like "32174,32176"
 * Output: postalCode IN ('32174','32176')
 */
function buildExactInClause(field: string, csvValues: string): string {
    const values = csvValues.split(",").map(v => v.trim()).filter(v => v !== "");
    if (values.length === 0) return "";

    const quoted = values.map(v => "'" + v.replace(/'/g, "\\'") + "'");
    return field + " IN (" + quoted.join(",") + ")";
}


/**
 * Builds a NOT IN clause with % wildcards for exclusion matching.
 * Input: comma-separated values like "fixer upper,as-is"
 * Output: remarks NOT IN ('%fixer upper%','%as-is%')
 */
function buildWildcardNotInClause(field: string, csvValues: string): string {
    const values = csvValues.split(",").map(v => v.trim()).filter(v => v !== "");
    if (values.length === 0) return "";

    const quoted = values.map(v => "'%" + v.replace(/'/g, "\\'") + "%'");
    return field + " NOT IN (" + quoted.join(",") + ")";
}

export function mapArrayToObject(base: ZIDXBase, arrData: string[], arrKeys: string[], obj: any) {
    for (let i = 0; i < arrData.length; i++) {
        switch (typeof obj[arrKeys[i]]) {
            case "number":
                obj[arrKeys[i]] = parseFloat(arrData[i]);
                break;
            default:
                obj[arrKeys[i]] = arrData[i];
                break;
        }
    }
    if(obj.photo=="") {
        if(base.zidx.options.isDarkMode) {
            obj.photo = base.zidx.options.staticDomain + base.zidx.getVersionURL("/static/images/image-not-available-dark.jpg", false);
        }else{
            obj.photo = base.zidx.options.staticDomain + base.zidx.getVersionURL("/static/images/image-not-available.jpg", false);
        }
    }
}

export function doSearchCountCallback(base: ZIDXBase, s: string) {
    console.log("doSearchCountCallback");
    let arr = s.split("\t");
    if (arr.length == 2) {
        if (arr[0] == "1") {
            let count=parseInt(arr[1]);
            if(count>10000) {
                $(".zidxSearchMorePopupResultCount", base.zidx.peakidxShadowRoot).html("Thousands of ");
            } else if(count>200){
                $(".zidxSearchMorePopupResultCount", base.zidx.peakidxShadowRoot).html("Many ");
            }else{
                $(".zidxSearchMorePopupResultCount", base.zidx.peakidxShadowRoot).html(""+count);
            }
        } else {
            if (base.zidx.options.debug) console.log("Search Error:", arr[1]);
        }
    }
}

export function doSearchCallback(base: ZIDXBase, s: string, offset: number) {
    console.log("doSearchCallback");
    if (base.resetListingData) {
        base.resetListingData=false;
        base.listingData = new ZIDXListingData();
        base.listingDataOffsetsLoaded = {};
        base.listingDataOffsetsLoading = {};
        base.currentOffset = 0;
        $(".zidxSearchResultsGridContainer", base.zidx.peakidxShadowRoot).scrollTop(0);
    }
    let arrLine = s.split("\n");
    let searchResultCount = arrLine.length;
    if (base.isFreeTrial() && searchResultCount > 20) {
        searchResultCount = Math.min(searchResultCount, 20);
    }
    let statusLine = arrLine[0].split("\t");
    if (statusLine[0] == "1") {
        base.listingData.totalListings = parseInt(statusLine[1]);
        if (base.isFreeTrial()) {
            base.listingData.totalListings = Math.min(base.listingData.totalListings, 20);
        }
        base.zidx.zidxBase.infiniteScroll?.setCount(base.listingData.totalListings);
        if(base.listingData.totalListings>10000) {
            $(".zidxSearchMorePopupResultCount", base.zidx.peakidxShadowRoot).html("Thousands of ");
        } else if(base.listingData.totalListings>200){
            $(".zidxSearchMorePopupResultCount", base.zidx.peakidxShadowRoot).html("Many ");
        }else{
            $(".zidxSearchMorePopupResultCount", base.zidx.peakidxShadowRoot).html(""+base.listingData.totalListings);
        }
    } else {
        if (base.zidx.options.debug) console.log("Search status: fail. Error Message: " + statusLine[1]);
        return;
    }
    let storedListings = 0;
    for (let i = 1; i < searchResultCount; i++) {
        if (arrLine[i].trim().length == 0) continue;
        let row = arrLine[i].split("\t");
        let listingSummary = new ZIDXListingSummary();
        mapArrayToObject(base, row, base.arrListingSummaryFieldOrder, listingSummary);
        listingSummary.fixCase();
        if(SavedListingRepository.getInstance(base.zidx).isHidden(listingSummary.indexId+"-"+listingSummary.id, listingSummary.uniqueId)){
            continue;
        }
        base.listingData.listings[offset + (storedListings)] = listingSummary;
        storedListings++;
    }
    if (base.zidx.options.debug) console.log("Search status: success." +
        " Total Matched Listings: " + base.listingData.totalListings +
        " Stored Listings:" + storedListings +
        " Total Loaded Listings:" + base.listingData.listings.length);
    updateListingData(base);
    if (base.infiniteScroll != null) {
        base.infiniteScroll.destroyPanels();
    }
    displayListings(base);
}

export function parseSearchData(base: ZIDXBase) {
    let searchDataElement = base.zidx.peakidxShadowRoot.getElementById("zidxSearchData");
    if(searchDataElement==null){
        return;
    }
    let s = searchDataElement.innerHTML;
    let arrLine = s.split("").reverse().join("").split("\n");
    let statusLine = arrLine[0].split("\t");
    // console.log("parseSearchData count", parseInt(statusLine[1]));
    if (statusLine[0] == "1") {
        base.listingData.totalListings = parseInt(statusLine[1]);
        if (base.isFreeTrial()) {
            base.listingData.totalListings = Math.min(base.listingData.totalListings, 20);
        }
        base.zidx.zidxBase.infiniteScroll?.setCount(base.listingData.totalListings);
    } else {
        return;
    }
    for (let i = 1; i < arrLine.length; i++) {
        if (arrLine[i].trim().length == 0) continue;
        let row = arrLine[i].split("\t");
        let listingSummary = new ZIDXListingSummary();
        mapArrayToObject(base, row, base.arrListingSummaryFieldOrder, listingSummary);
        listingSummary.fixCase();
        if(SavedListingRepository.getInstance(base.zidx).isHidden(listingSummary.indexId+"-"+listingSummary.id, listingSummary.uniqueId)){
            continue;
        }
        base.listingData.listings.push(listingSummary);
    }
    updateListingData(base);
}

export function updateListingData(base: ZIDXBase) {
    base.listingData.locationCount = 0;
    base.listingData.averageLatitude = 0;
    base.listingData.averageLongitude = 0;
    base.listingData.minLongitude = 0;
    base.listingData.maxLongitude = 0;
    base.listingData.minLatitude = 0;
    base.listingData.maxLatitude = 0;
    for (let i = 0; i < base.listingData.listings.length; i++) {
        let listingSummary = base.listingData.listings[i];
        if (typeof listingSummary == "undefined") {
            continue;
        }
        if (listingSummary.latitude != 0) {
            if (base.listingData.minLatitude == 0) {
                base.listingData.minLatitude = listingSummary.latitude;
            } else {
                base.listingData.minLatitude = Math.min(base.listingData.minLatitude, listingSummary.latitude);
            }
            if (base.listingData.minLongitude == 0) {
                base.listingData.minLongitude = listingSummary.longitude;
            } else {
                base.listingData.minLongitude = Math.min(base.listingData.minLongitude, listingSummary.longitude);
            }
            base.listingData.maxLatitude = Math.max(base.listingData.maxLatitude, listingSummary.latitude);
            base.listingData.maxLongitude = Math.max(base.listingData.maxLongitude, listingSummary.longitude);
            base.listingData.averageLatitude += listingSummary.latitude;
            base.listingData.averageLongitude += listingSummary.longitude;
            base.listingData.locationCount++;
        }
    }
    if (base.listingData.locationCount != 0) {
        base.listingData.averageLatitude /= base.listingData.locationCount;
        base.listingData.averageLongitude /= base.listingData.locationCount;
    }
}

export function doSearchCallbackError(base: ZIDXBase, r: any) {
    delete base.listingDataOffsetsLoading[base.currentOffset];
}

export function doSearchCountCallbackError(base: ZIDXBase, r: any) {
}

export function parseListingIds(s:string):string{
    let arrId = String(s).toLowerCase().split(",");
    let arrListingId: string[] = [];
    let arrListingTableId: string[] = [];
    for (let n2 = 0; n2 < arrId.length; n2++) {
        let id = arrId[n2];
        let arrChar = [];
        let arrInt = [];
        for (let n = 0; n < id.length; n++) {
            if (isNaN(parseInt(id.charAt(n)))) {
                let charCode = id.toLowerCase().charCodeAt(n);
                if (charCode >= 97 && charCode <= 122) {
                    arrChar.push(id.charAt(n).toLowerCase());
                }
            } else {
                arrInt.push(id.charAt(n));
            }
        }
        if (arrInt.length > 0) {
            arrListingId.push(arrInt.join(""));
            arrListingTableId.push(arrChar.join(""));
        }
    }
    return "|O" + arrListingId.join(",") + "|P" + arrListingTableId.join(",");
}

export function buildJsonSearch(base: ZIDXBase, ss: ZIDXSearchFields, countOnly: boolean): any {
    const sp = new SearchParams();

    switch (ss.zidx_search_sort) {
        case 0: sp.sort = "newest"; break;
        case 1: sp.sort = "price_asc"; break;
        case 2: sp.sort = "price_desc"; break;
        case 3: sp.sort = "relevance"; break;
    }
    sp.offset = ss.zidx_offset;
    sp.idx_site_id = base.zidx.options.siteId;
    sp.countOnly = countOnly;
    sp.querySummary=base.aiSearchIntegration?.querySummary??"";
    sp.aiMode = base.aiSearchIntegration?.isActive() ?? false;

    const locationMode = ($("#zidx_search_location_mode", base.zidx.peakidxShadowRoot).val() || "location") as string;
    const locationTagsJson = ($("#zidx_search_location_tags", base.zidx.peakidxShadowRoot).val() || "") as string;
    let locationTags: { field: string; value: string }[] = [];
    let tags=locationTagsJson.split(",");
    for(let i=0;i<tags.length;i++){
        let tag=tags[i].split(":");
        if(tag.length==2) {
            locationTags.push({field: tag[0], value: tag[1].trim()});
        }
    }

    if (ss.zidx_search_county != "") sp.county = ss.zidx_search_county;
    if (ss.zidx_search_subdivision != "") sp.subdivision = ss.zidx_search_subdivision;
    if (ss.zidx_search_postal_code != "") sp.postalCode = ss.zidx_search_postal_code;
    if (ss.zidx_search_address != "") sp.address = ss.zidx_search_address;

    if (ss.zidx_search_building != "") sp.building = ss.zidx_search_building;
    if (ss.zidx_search_exclude_remarks != "") sp.excludeRemarks = ss.zidx_search_exclude_remarks;

    if (ss.zidx_search_open_houses == 1) sp.openHouses = true;

    if (typeof ss.zidx_search_show_listings != "undefined" && ss.zidx_search_show_listings != "0" && ss.zidx_search_show_listings != "All") {
        sp.showListings = parseInt(ss.zidx_search_show_listings);
    }
    sp.layout = ss.zidx_layout;

    if (ss.zidx_search_price_from != 0 || ss.zidx_search_price_to != 2000000000) {
        sp.price = { min: ss.zidx_search_price_from, max: ss.zidx_search_price_to };
    }
    if (ss.zidx_search_bedrooms_from != 0 || ss.zidx_search_bedrooms_to != 100) {
        sp.beds = { min: ss.zidx_search_bedrooms_from, max: ss.zidx_search_bedrooms_to };
    }
    if (ss.zidx_search_bathrooms_from != 0 || ss.zidx_search_bathrooms_to != 100) {
        sp.baths = { min: ss.zidx_search_bathrooms_from, max: ss.zidx_search_bathrooms_to };
    }
    if (ss.zidx_search_sqft_from != 0 || ss.zidx_search_sqft_to != 1000000000) {
        sp.sqft = { min: ss.zidx_search_sqft_from, max: ss.zidx_search_sqft_to };
    }
    if (ss.zidx_search_acreage_from != 0 || ss.zidx_search_acreage_to != 1000000000) {
        sp.acreage = { min: ss.zidx_search_acreage_from, max: ss.zidx_search_acreage_to };
    }
    if (ss.zidx_search_year_built_from != 0 || ss.zidx_search_year_built_to != 10000) {
        sp.yearBuilt = { min: ss.zidx_search_year_built_from, max: ss.zidx_search_year_built_to };
    }
    if (ss.zidx_search_timestamp_from != 0 || ss.zidx_search_timestamp_to != 2100000000) {
        sp.timestamp = { min: ss.zidx_search_timestamp_from, max: ss.zidx_search_timestamp_to };
    }

    if (ss.zidx_search_polygon != "") {
        let points = ss.zidx_search_polygon.split(",");
        sp.polygons = [];
        let currentPolygon: number[] = [];
        for (let i = 0; i < points.length; i++) {
            if (points[i] == "p") {
                currentPolygon = [];
                sp.polygons.push(currentPolygon);
            } else {
                currentPolygon.push(parseFloat(points[i]));
            }
        }
    }

    if (ss.zidx_search_listing_id != "") {
        sp.mlsNumbers = ss.zidx_search_listing_id.split(",");
    }

    const aiActive = base.aiSearchIntegration?.isActive() ?? false;
    const aiQueries = base.aiSearchIntegration?.aiAdvancedSearchQueries;
    const aiVectorQueries = base.aiSearchIntegration?.aiVectorSearchQueries;

    let vectorSearch = ss.zidx_search_remarks;
    let negativeVectorSearch = ss.zidx_search_exclude_remarks;
    if (aiActive && aiVectorQueries && aiVectorQueries.size > 0) {
        // In AI mode, use the first available AI vector query
        vectorSearch = aiVectorQueries.values().next().value!;
    }
    if (vectorSearch) {
        sp.vectorSearch = vectorSearch;
    }
    if (negativeVectorSearch) {
        sp.negativeVectorSearch = negativeVectorSearch;
    }

    let advancedSearchParams: string[] = [];

    function addSearchParam(fieldName: string, searchField: string): void {
        if (ss[fieldName] != "") {
            let param = searchField + " IN (";
            let values = (ss[fieldName] as string).split(",");
            for (let i = 0; i < values.length; i++) {
                values[i] = values[i].trim();
                if (i != 0) param += ",";
                param += "'" + values[i].replace(/'/g, "\\'") + "'";
            }
            param += ")";
            advancedSearchParams.push(param);
        }
    }

    if (!aiActive) {
        // NON-AI MODE: build advancedSearchQuery from form checkboxes only
        // (numeric ranges stay as top-level fields — existing behavior)
        addSearchParam("zidx_search_type", "type");
        addSearchParam("zidx_search_frontage", "frontage");
        addSearchParam("zidx_search_sub_type", "subType");
        addSearchParam("zidx_search_style", "style");
        addSearchParam("zidx_search_view", "view");
        addSearchParam("zidx_search_pool", "pool");
        addSearchParam("zidx_search_parking", "parking");
        addSearchParam("zidx_search_condition", "condition");
        addSearchParam("zidx_search_status", "status");
        addSearchParam("zidx_search_list_status", "listingStatus");

        console.log("buildJsonSearch", locationMode, locationTags);
        if (locationMode === "location" && locationTags.length > 0) {
            const byField: Record<string, string[]> = {};
            for (const loc of locationTags) {
                if (!byField[loc.field]) byField[loc.field] = [];
                byField[loc.field].push("'" + loc.value.replace(/'/g, "\\'") + "'");
            }
            const clauses: string[] = [];
            for (const field in byField) {
                clauses.push(field + " IN (" + byField[field].join(",") + ")");
            }
            const locationClause = clauses.length === 1
                ? clauses[0]
                : "(" + clauses.join(" OR ") + ")";
            advancedSearchParams.push(locationClause);
        }
    } else {
        // AI MODE: omit top-level numeric/string fields from SearchParams.
        // They'll be encoded into advancedSearchQuery instead.
        // Clear top-level ranges so they don't double-filter on the C++ side.
        sp.price = null;
        sp.beds = null;
        sp.baths = null;
        sp.sqft = null;
        sp.acreage = null;
        sp.yearBuilt = null;
        sp.subdivision = "";
        sp.address = "";
        sp.building = "";
        sp.postalCode = "";
        sp.excludeRemarks = "";
        // county is handled via advancedSearchQuery in AI mode too
        sp.county = "";
    }

    let formDerivedAdvancedQuery = advancedSearchParams.join(" AND ");

    // ── Near location (unchanged — works in both modes) ──
    if (locationMode === "nearLocation" && ss.zidx_nearlocation_address != "") {
        let bounds = base.zidx.zidxMap.getRectangleBounds(
            ss.zidx_nearlocation_latitude,
            ss.zidx_nearlocation_longitude,
            ss.zidx_nearlocation_distanceInMiles
        );
        sp.nearLocation = {
            enabled: true,
            address: ss.zidx_nearlocation_address,
            latitude: {
                min: base.zidx.zidxMap.getLatitudeInteger(bounds.minLat),
                max: base.zidx.zidxMap.getLatitudeInteger(bounds.maxLat)
            },
            longitude: {
                min: base.zidx.zidxMap.getLongitudeInteger(bounds.minLng),
                max: base.zidx.zidxMap.getLongitudeInteger(bounds.maxLng)
            },
            distanceInMiles: ss.zidx_nearlocation_distanceInMiles,
        };
    }

    // ── Build listing indices ──
    let indexIds = ($("#zidx_listing_indices", base.zidx.peakidxShadowRoot).val()! as string).split(",");
    for (let i = 0; i < indexIds.length; i++) {
        const listingIndexId = parseInt(indexIds[i]);

        let advancedQuery: string;
        if (aiActive && aiQueries && aiQueries.has(listingIndexId)) {
            // AI MODE: Take the AI-generated query and merge in form-derived
            // numeric/string fields using the unified query builder.
            advancedQuery = buildUnifiedAdvancedQuery(base, ss, aiQueries.get(listingIndexId)!);
        } else if (aiActive && aiQueries && aiQueries.size > 0) {
            // AI mode but no query for this specific index — use first available
            advancedQuery = buildUnifiedAdvancedQuery(base, ss, aiQueries.values().next().value!);
        } else {
            // Non-AI mode: use form-derived query (enum checkboxes only)
            advancedQuery = formDerivedAdvancedQuery;
        }

        sp.listingIndices.push({
            listingIndexId: listingIndexId,
            advancedSearchQuery: advancedQuery
        });
    }

    if (ss.zidx_layout == LISTING_LAYOUT_MAP) {
        if (ss.zidx_search_latitude != "") {
            let latitude = ss.zidx_search_latitude.split(",");
            let longitude = ss.zidx_search_longitude.split(",");
            sp.searchWithinMap = {
                enabled: true,
                latitude: { min: parseInt(latitude[0]), max: parseInt(latitude[1]) },
                longitude: { min: parseInt(longitude[0]), max: parseInt(longitude[1]) },
            };
        }
    }
    if (aiActive && base.aiSearchIntegration?.aiFieldWeights) {
        const fw = base.aiSearchIntegration.aiFieldWeights;
        if (Object.keys(fw).length > 0) {
            sp.fieldWeights = fw;
        }
    }

    console.log("build json", sp, sp.toJSON());
    return sp.toJSON();
}

export function setCheckboxValues(base: ZIDXBase, fieldName: string, values: string[]) {
    $("input[name='" + fieldName + "'], input[name='" + fieldName.replace("zidx_", "zidx_more_") + "']", base.zidx.peakidxShadowRoot).each(function (this) {
        for (let i = 0; i < values.length; i++) {
            let value = values[i];
            if ((this as HTMLInputElement).value == value) {
                (this as HTMLInputElement).checked = true;
                return;
            }
        }
    });
}

export function decodeNativeSearch(base: ZIDXBase, qs: string, isJSON: boolean, callback?: () => void) {
    const js = JSON.parse(qs);
    const sp = SearchParams.fromJSON(js);
    applySearchParamsToForm(base, sp, callback);
    $("#zidx_offset", base.zidx.peakidxShadowRoot).val(0);
}

export function applySearchParamsToForm(base: ZIDXBase, sp: SearchParams, callback?: () => void) {
    const root = base.zidx.peakidxShadowRoot;

    const setField = (id: string, val: string) => {
        $("#" + id, root).val(val);
        $("#" + id.replace("zidx_", "zidx_more_"), root).val(val);
    };

    const setRange = (rangeBase: string, range: RangeParam) => {
        const setWithFallback = (selector: string, value: number) => {
            let $el = $(selector, root);
            if ($el.length > 0) {
                $el.val(value);
                if (($el[0] as HTMLSelectElement).selectedIndex == -1) {
                    ($el[0] as HTMLSelectElement).selectedIndex = 0;
                }
            }
        };
        setWithFallback("#" + rangeBase + "_from", range.min);
        setWithFallback("#" + rangeBase + "_to", range.max);
        setWithFallback("#" + rangeBase.replace("zidx_", "zidx_more_") + "_from", range.min);
        setWithFallback("#" + rangeBase.replace("zidx_", "zidx_more_") + "_to", range.max);
    };

    const sortMap: Record<string, number> = {
        "newest": 0, "price_asc": 1, "price_desc": 2, "relevance": 3,
    };
    if (sp.sort in sortMap) {
        setField("zidx_search_sort", String(sortMap[sp.sort]));
    }

    if (sp.city != "") setCheckboxValues(base, "zidx_search_city", sp.city.split(","));
    if (sp.county != "") setCheckboxValues(base, "zidx_search_county", sp.county.split(","));
    if (sp.subdivision != "") setField("zidx_search_subdivision", sp.subdivision);
    if (sp.postalCode != "") setField("zidx_search_postal_code", sp.postalCode);
    if (sp.address != "") setField("zidx_search_address", sp.address);
    if (sp.building != "") setField("zidx_search_building", sp.building);
    if (sp.excludeRemarks != "") setField("zidx_search_exclude_remarks", sp.excludeRemarks);
    if (sp.negativeVectorSearch != "" && sp.excludeRemarks == "") {
        setField("zidx_search_exclude_remarks", sp.negativeVectorSearch);
    }
    if (sp.openHouses) {
        setCheckboxValues(base, "zidx_search_open_houses", ["1"]);
    }

    if (sp.showListings != 0) {
        setCheckboxValues(base, "zidx_search_show_listings", [""+sp.showListings]);
    }

    if (sp.price) setRange("zidx_search_price", sp.price);
    if (sp.beds) setRange("zidx_search_bedrooms", sp.beds);
    if (sp.baths) setRange("zidx_search_bathrooms", sp.baths);
    if (sp.sqft) setRange("zidx_search_sqft", sp.sqft);
    if (sp.acreage) setRange("zidx_search_acreage", sp.acreage);
    if (sp.yearBuilt) setRange("zidx_search_year_built", sp.yearBuilt);

    if (sp.polygons) {
        let parts: string[] = [];
        for (const poly of sp.polygons) {
            parts.push("p");
            for (const coord of poly) {
                parts.push(String(coord));
            }
        }
        setField("zidx_search_polygon", parts.join(","));
    }

    if (sp.mlsNumbers) {
        setField("zidx_search_listing_id", sp.mlsNumbers.join(","));
    }

    if (sp.searchWithinMap) {
        if (sp.searchWithinMap.enabled) {
            setField("zidx_search_latitude", sp.searchWithinMap.latitude.min + "," + sp.searchWithinMap.latitude.max);
            setField("zidx_search_longitude", sp.searchWithinMap.longitude.min + "," + sp.searchWithinMap.longitude.max);
        }
    }

    const advancedFieldToForm: Record<string, string> = {
        "type": "zidx_search_type",
        "frontage": "zidx_search_frontage",
        "subType": "zidx_search_sub_type",
        "style": "zidx_search_style",
        "view": "zidx_search_view",
        "pool": "zidx_search_pool",
        "parking": "zidx_search_parking",
        "condition": "zidx_search_condition",
        "status": "zidx_search_status",
        "listingStatus": "zidx_search_list_status",
    };
    setCheckboxValues(base, "zidx_search_show_listings", [""+sp.showListings]);
    if (sp.listingIndices.length > 0) {
        const advQuery = sp.listingIndices[0].advancedSearchQuery;
        if (advQuery != "") {
            // BETWEEN: field BETWEEN (min, max)
            const betweenRx = /(\w[\w\s]*?)\s+BETWEEN\s+\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*\)/gi;
            let bm;
            while ((bm = betweenRx.exec(advQuery)) !== null) {
                const field = bm[1].trim().toLowerCase();
                const min = parseFloat(bm[2]);
                const max = parseFloat(bm[3]);
                applyNumericRangeToForm(field, min, max, sp, setRange);
            }

            // >= and <=
            const compRx = /(\w[\w\s]*?)\s*(>=|<=)\s*(\d+(?:\.\d+)?)/g;
            let cm;
            while ((cm = compRx.exec(advQuery)) !== null) {
                const field = cm[1].trim().toLowerCase();
                const op = cm[2];
                const val = parseFloat(cm[3]);
                if (op === ">=") {
                    applyNumericRangeToForm(field, val, undefined, sp, setRange);
                } else {
                    applyNumericRangeToForm(field, undefined, val, sp, setRange);
                }
            }

            // Wildcard IN for string fields: field IN ('%value%')
            const wildcardInRx = /(\w[\w\s]*?)\s+IN\s+\(([^)]+)\)/gi;
            let wm;
            while ((wm = wildcardInRx.exec(advQuery)) !== null) {
                const field = wm[1].trim().toLowerCase();
                const rawValues = wm[2];
                // Check if values contain % — if so, it's a wildcard string field
                if (rawValues.indexOf('%') !== -1) {
                    const values = rawValues.split(",")
                        .map(v => v.trim().replace(/^'|'$/g, '').replace(/%/g, '').trim())
                        .filter(v => v !== "");
                    const csvValue = values.join(",");
                    const fieldToFormId: Record<string, string> = {
                        "subdivision": "zidx_search_subdivision",
                        "address": "zidx_search_address",
                        "building": "zidx_search_building",
                        "postalcode": "zidx_search_postal_code",
                    };
                    if (fieldToFormId[field]) {
                        setField(fieldToFormId[field], csvValue);
                    }
                }
            }

            // NOT IN for exclude remarks: remarks NOT IN ('%value%')
            const notInRx = /remarks\s+NOT\s+IN\s+\(([^)]+)\)/gi;
            let nm;
            while ((nm = notInRx.exec(advQuery)) !== null) {
                const rawValues = nm[1];
                const values = rawValues.split(",")
                    .map(v => v.trim().replace(/^'|'$/g, '').replace(/%/g, '').trim())
                    .filter(v => v !== "");
                setField("zidx_search_exclude_remarks", values.join(","));
            }
        }
    }

    if (base.locationMegaMenu) {
        for (let i = 0; i < sp.listingIndices.length; i++) {
            let query = sp.listingIndices[i].advancedSearchQuery;
            let records = parseInQuery(query);
            for (let field in records) {
                for (let n = 0; n < records[field].length; n++) {
                    let validField = false;
                    switch (field) {
                        case "city":
                        case "postCode":
                        case "address":
                        case "subdivision":
                        case "county":
                            validField = true;
                            break;
                    }
                    if (validField) {
                        base.locationMegaMenu.addLocation(field, records[field][n]);
                    }
                }
            }
        }
    }

    if (sp.nearLocation) {
        const hasCoords = sp.nearLocation.latitude &&
            sp.nearLocation.longitude &&
            (sp.nearLocation.latitude.min !== 0 || sp.nearLocation.latitude.max !== 0) &&
            (sp.nearLocation.longitude.min !== 0 || sp.nearLocation.longitude.max !== 0);

        if (hasCoords) {
            applyNearLocation(base, sp, setField);
            $("#zidx_offset", root).val(sp.offset);
            applyLayout(base, sp);
            if (sp.aiMode && base.aiSearchIntegration) {
                base.aiSearchIntegration.activateFromSavedSearch(sp);
            }
            if (callback) callback();
        } else if (sp.nearLocation.address && sp.nearLocation.address !== "") {
            const distanceInMiles = sp.nearLocation.distanceInMiles || 5;
            geocodeAddress(
                base,
                sp.nearLocation.address,
                (lat: number, lng: number) => {
                    let bounds = base.zidx.zidxMap.getRectangleBounds(lat, lng, distanceInMiles);
                    sp.nearLocation = {
                        enabled: true,
                        address: sp.nearLocation!.address,
                        latitude: {
                            min: base.zidx.zidxMap.getLatitudeInteger(bounds.minLat),
                            max: base.zidx.zidxMap.getLatitudeInteger(bounds.maxLat)
                        },
                        longitude: {
                            min: base.zidx.zidxMap.getLongitudeInteger(bounds.minLng),
                            max: base.zidx.zidxMap.getLongitudeInteger(bounds.maxLng)
                        },
                        distanceInMiles: distanceInMiles,
                    };
                    applyNearLocation(base, sp, setField);

                    $("#zidx_offset", root).val(sp.offset);
                    applyLayout(base, sp);
                    if (sp.aiMode && base.aiSearchIntegration) {
                        base.aiSearchIntegration.activateFromSavedSearch(sp);
                    }
                    if (callback) callback();
                },
                () => {
                    console.warn("[applySearchParamsToForm] Geocode failed for:", sp.nearLocation!.address);
                    setField("zidx_nearlocation_address", sp.nearLocation!.address);

                    $("#zidx_offset", root).val(sp.offset);
                    applyLayout(base, sp);
                    if (sp.aiMode && base.aiSearchIntegration) {
                        base.aiSearchIntegration.activateFromSavedSearch(sp);
                    }
                    if (callback) callback();
                }
            );
            return;
        } else {
            $("#zidx_offset", root).val(sp.offset);
            applyLayout(base, sp);
            if (sp.aiMode && base.aiSearchIntegration) {
                base.aiSearchIntegration.activateFromSavedSearch(sp);
            }
            if (callback) callback();
        }
    } else {
        $("#zidx_offset", root).val(sp.offset);
        applyLayout(base, sp);
        if (sp.aiMode && base.aiSearchIntegration) {
            base.aiSearchIntegration.activateFromSavedSearch(sp);
        }
        if (callback) callback();
    }
}

function applyNumericRangeToForm(
    field: string,
    min: number | undefined,
    max: number | undefined,
    sp: SearchParams,
    setRange: (rangeBase: string, range: RangeParam) => void
) {
    const fieldMap: Record<string, { formBase: string; spField: string; defaultMin: number; defaultMax: number }> = {
        "price":     { formBase: "zidx_search_price",     spField: "price",     defaultMin: 0, defaultMax: 2000000000 },
        "beds":      { formBase: "zidx_search_bedrooms",   spField: "beds",      defaultMin: 0, defaultMax: 100 },
        "bedrooms":  { formBase: "zidx_search_bedrooms",   spField: "beds",      defaultMin: 0, defaultMax: 100 },
        "baths":     { formBase: "zidx_search_bathrooms",  spField: "baths",     defaultMin: 0, defaultMax: 100 },
        "bathrooms": { formBase: "zidx_search_bathrooms",  spField: "baths",     defaultMin: 0, defaultMax: 100 },
        "sqft":      { formBase: "zidx_search_sqft",       spField: "sqft",      defaultMin: 0, defaultMax: 1000000000 },
        "acreage":   { formBase: "zidx_search_acreage",    spField: "acreage",   defaultMin: 0, defaultMax: 1000000000 },
        "yearbuilt": { formBase: "zidx_search_year_built", spField: "yearBuilt", defaultMin: 0, defaultMax: 10000 },
    };

    const mapping = fieldMap[field];
    if (!mapping) return;

    // Don't overwrite if the top-level field already set this range
    if ((sp as any)[mapping.spField]) return;

    const range: RangeParam = {
        min: min !== undefined ? min : mapping.defaultMin,
        max: max !== undefined ? max : mapping.defaultMax,
    };
    setRange(mapping.formBase, range);
}

export function applyNearLocation(base: ZIDXBase, sp: SearchParams, setField: (id: string, val: string) => void) {
    if (!sp.nearLocation) return;

    setField("zidx_nearlocation_address", sp.nearLocation.address);

    let circle = base.zidx.zidxMap.getCircleFromBounds({
        minLat: sp.nearLocation.latitude.min,
        maxLat: sp.nearLocation.latitude.max,
        minLng: sp.nearLocation.longitude.min,
        maxLng: sp.nearLocation.longitude.max,
    });
    let lat = base.zidx.zidxMap.getLatitudeFromInteger(circle.lat);
    let lng = base.zidx.zidxMap.getLongitudeFromInteger(circle.lng);
    let selectedPlace = {
        formattedAddress: sp.nearLocation.address,
        lat: lat,
        lng: lng
    };
    if (base.locationMegaMenu) {
        $("#zidxLocationAddressInput", base.zidx.peakidxShadowRoot).val(sp.nearLocation.address);
        let radiusSelect = $("#zidxLocationRadiusSelect", base.zidx.peakidxShadowRoot)[0] as HTMLSelectElement;
        for (let i = 0; i < radiusSelect.options.length; i++) {
            if (radiusSelect.options[i].value == sp.nearLocation.distanceInMiles + "") {
                radiusSelect.selectedIndex = i;
            }
        }
        base.locationMegaMenu.selectedPlace = selectedPlace;
        base.locationMegaMenu.applyNearLocation();
        base.locationMegaMenu.switchTab("nearLocation");
    }
}

export function applyLayout(base: ZIDXBase, sp: SearchParams) {
    if (sp.layout != 0) {
        if (sp.layout == 0) {
            $(".zidxSearchListLink", base.zidx.peakidxShadowRoot).trigger("click");
        } else if (sp.layout == 2) {
            $(".zidxSearchMapLink", base.zidx.peakidxShadowRoot).trigger("click");
        } else if (sp.layout == 1) {
            $(".zidxSearchDetailLink", base.zidx.peakidxShadowRoot).trigger("click");
        }
    }
}

export function geocodeAddress(base: ZIDXBase, address: string, onSuccess: (lat: number, lng: number) => void, onError: () => void) {
    const siteDomain = base.zidx.options.siteDomain;

    fetch(siteDomain + "/geocode-api-nonce", {
        method: "GET",
        credentials: "include",
    })
        .then(response => {
            if (!response.ok) throw new Error("Nonce request failed: " + response.status);
            return response.json();
        })
        .then(nonceData => {
            if (!nonceData.nonce) throw new Error("No nonce in response");
            const params = new URLSearchParams();
            params.set("address", address);
            params.set("nonce", nonceData.nonce);
            return fetch(siteDomain + "/geocode-api/?" + params.toString(), {
                method: "GET",
                credentials: "include",
            });
        })
        .then(response => {
            if (!response.ok) throw new Error("Geocode request failed: " + response.status);
            return response.json();
        })
        .then(data => {
            if (data.latitude && data.longitude) {
                onSuccess(data.latitude, data.longitude);
            } else {
                onError();
            }
        })
        .catch(err => {
            console.error("[geocodeAddress] Error:", err);
            onError();
        });
}

export function parseInQuery(query: string): ZIDXAdvancedQueryResult {
    const result: ZIDXAdvancedQueryResult = {};

    // Match IN / NOT IN clauses (existing + wildcard values)
    const inPattern = /(\w[\w\s]*?)\s+IN\s+\(([^)]+)\)/gi;
    let match;
    while ((match = inPattern.exec(query)) !== null) {
        const field = match[1].trim();
        const valuesString = match[2];
        const values = valuesString
            .split(',')
            .map(v => v.trim().replace(/^'|'$/g, '').replace(/%/g, ''));

        if (result[field]) {
            result[field] = [...new Set([...result[field], ...values])];
        } else {
            result[field] = values;
        }
    }

    // Match BETWEEN clauses: field BETWEEN (min, max)
    const betweenPattern = /(\w[\w\s]*?)\s+BETWEEN\s+\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*\)/gi;
    while ((match = betweenPattern.exec(query)) !== null) {
        const field = match[1].trim();
        result[field] = [match[2], match[3]]; // [min, max] as strings
    }

    // Match comparison operators: field >= value, field <= value, etc.
    const compPattern = /(\w[\w\s]*?)\s*(>=|<=|>|<|=)\s*(\d+(?:\.\d+)?)/g;
    while ((match = compPattern.exec(query)) !== null) {
        const field = match[1].trim();
        const op = match[2];
        const val = match[3];

        // Skip if this field was already captured by BETWEEN or IN
        if (result[field]) continue;

        result[field] = [op + val]; // e.g., [">=300000"]
    }

    return result;
}

export async function doSearch(base: ZIDXBase, postObj: ZIDXSearchFields, forceSearch:boolean) {
    if (postObj.zidx_layout == LISTING_LAYOUT_MAP) {
        postObj.mapWidth = 100;
        postObj.mapHeight = 100;
    }
    let countOnly=false;
    // if (!forceSearch && (base.searchMorePopupOpen || $(".zidxSiteHomeContainer.zidxPageActive", base.zidx.peakidxShadowRoot).length>0)) {
    //     countOnly=true;
    // }
    let json = buildJsonSearch(base, postObj, countOnly);
    if(!forceSearch && base.json==json){
        return;
    }
    let contactId=base.zidx.getCookie("ZIDXCONTACTID");
    if(contactId!="" && contactId!="0") {
        json.contact_id = parseInt(contactId);
    }
    base.json=JSON.stringify(json);
    let url = base.zidx.options.siteDomain + "/json-search/";
    if (!countOnly) {
        url += "?submit=1";
    }
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: base.json
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.log("json search error response text", errorText);
            return;
        }

        const result = await response.text();
        let jsonResponse = JSON.parse(result);
        if (jsonResponse.success) {
            // console.log("json search response count:", jsonResponse.count);
            base.listingDataOffsetsLoaded[postObj.zidx_offset] = true;
            doSearchCallback(base, jsonResponse.results, postObj.zidx_offset);
        } else {
            console.log("json search invalid response", result);
            delete base.listingDataOffsetsLoading[postObj.zidx_offset];
            doSearchCallbackError(base, result);
        }
    } catch (error) {
        // Handles network failures or DNS issues
    }
    return;
}

export function dedupeSearchForm(base: ZIDXBase, postObj: any): ZIDXSearchFields {
    let searchFields = new ZIDXSearchFields();
    searchFields.zidx_offset = parseInt(postObj.zidx_offset);
    if (isNaN(searchFields.zidx_offset)) searchFields.zidx_offset = 0;
    if (typeof postObj.zidx_more_search_list_status != "undefined") {
        searchFields.zidx_search_list_status = postObj.zidx_more_search_list_status;
    }
    if (typeof postObj.zidx_search_location_tags != "undefined") {
        searchFields.zidx_search_location_tags = postObj.zidx_search_location_tags;
    }
    if (typeof postObj.zidx_search_location_mode != "undefined") {
        searchFields.zidx_search_location_mode = postObj.zidx_search_location_mode;
    }
    if (typeof postObj.zidx_more_search_type != "undefined") {
        searchFields.zidx_search_type = postObj.zidx_more_search_type;
    }
    if (typeof postObj.zidx_more_search_sub_type != "undefined") {
        searchFields.zidx_search_sub_type = postObj.zidx_more_search_sub_type;
    }
    if (typeof postObj.zidx_more_search_county != "undefined") {
        searchFields.zidx_search_county = postObj.zidx_more_search_county;
    }
    if (typeof postObj.zidx_more_search_parking != "undefined") {
        searchFields.zidx_search_parking = postObj.zidx_more_search_parking;
    }
    if (typeof postObj.zidx_more_search_frontage != "undefined") {
        searchFields.zidx_search_frontage = postObj.zidx_more_search_frontage;
    }
    if (typeof postObj.zidx_more_search_style != "undefined") {
        searchFields.zidx_search_style = postObj.zidx_more_search_style;
    }
    if (typeof postObj.zidx_more_search_view != "undefined") {
        searchFields.zidx_search_view = postObj.zidx_more_search_view;
    }
    if (typeof postObj.zidx_more_search_status != "undefined") {
        searchFields.zidx_search_status = postObj.zidx_more_search_status;
    }
    if (typeof postObj.zidx_more_search_condition != "undefined") {
        searchFields.zidx_search_condition = postObj.zidx_more_search_condition;
    }
    if (typeof postObj.zidx_more_search_pool != "undefined") {
        searchFields.zidx_search_pool = postObj.zidx_more_search_pool;
    }
    if (typeof postObj.zidx_more_search_building != "undefined") {
        searchFields.zidx_search_building = postObj.zidx_more_search_building;
    }
    if (typeof postObj.zidx_search_open_houses != "undefined" && postObj.zidx_search_open_houses=="1") {
        searchFields.zidx_search_open_houses=1;
    }else{
        searchFields.zidx_search_open_houses=0;
    }
    searchFields.zidx_search_polygon = postObj.zidx_search_polygon;
    searchFields.zidx_search_sort = parseInt(postObj.zidx_search_sort);
    if (isNaN(searchFields.zidx_search_sort)) searchFields.zidx_search_sort = 0;
    searchFields.zidx_layout = parseInt(postObj.zidx_layout);
    searchFields.zidx_search_latitude = postObj.zidx_search_latitude;
    searchFields.zidx_search_longitude = postObj.zidx_search_longitude;
    if (typeof postObj.zidx_search_location != "undefined") {
        searchFields.zidx_search_location = postObj.zidx_search_location;
    }
    if (typeof postObj.zidx_search_price_from == "undefined") {
        if (postObj.zidx_more_search_price_from != "") {
            searchFields.zidx_search_price_from = parseInt(postObj.zidx_more_search_price_from);
        }
        if (postObj.zidx_more_search_price_to != "") {
            searchFields.zidx_search_price_to = parseInt(postObj.zidx_more_search_price_to);
        }
        if (postObj.zidx_more_search_bedrooms_from != "") {
            searchFields.zidx_search_bedrooms_from = parseInt(postObj.zidx_more_search_bedrooms_from);
        }
        if (postObj.zidx_more_search_bedrooms_to != "") {
            searchFields.zidx_search_bedrooms_to = parseInt(postObj.zidx_more_search_bedrooms_to);
        }
        if (postObj.zidx_more_search_bathrooms_from != "") {
            searchFields.zidx_search_bathrooms_from = parseInt(postObj.zidx_more_search_bathrooms_from);
        }
        if (postObj.zidx_more_search_bathrooms_to != "") {
            searchFields.zidx_search_bathrooms_to = parseInt(postObj.zidx_more_search_bathrooms_to);
        }
    } else {
        if (postObj.zidx_search_price_from != "") {
            searchFields.zidx_search_price_from = parseInt(postObj.zidx_search_price_from);
        }
        if (postObj.zidx_search_price_to != "") {
            searchFields.zidx_search_price_to = parseInt(postObj.zidx_search_price_to);
        }
        if (postObj.zidx_search_bedrooms_from != "") {
            searchFields.zidx_search_bedrooms_from = parseInt(postObj.zidx_search_bedrooms_from);
        }
        if (postObj.zidx_search_bedrooms_to != "") {
            searchFields.zidx_search_bedrooms_to = parseInt(postObj.zidx_search_bedrooms_to);
        }
        if (postObj.zidx_search_bathrooms_from != "") {
            searchFields.zidx_search_bathrooms_from = parseInt(postObj.zidx_search_bathrooms_from);
        }
        if (postObj.zidx_search_bathrooms_to != "") {
            searchFields.zidx_search_bathrooms_to = parseInt(postObj.zidx_search_bathrooms_to);
        }
    }
    if(searchFields.zidx_search_price_to==0){
        searchFields.zidx_search_price_to=2000000000;
    }
    if(searchFields.zidx_search_bedrooms_to==0){
        searchFields.zidx_search_bedrooms_to=100;
    }
    if(searchFields.zidx_search_bathrooms_to==0){
        searchFields.zidx_search_bathrooms_to=100;
    }
    if (postObj.zidx_more_search_acreage_from != "") {
        searchFields.zidx_search_acreage_from = parseInt(postObj.zidx_more_search_acreage_from);
    }
    if (postObj.zidx_more_search_acreage_to != "") {
        searchFields.zidx_search_acreage_to = parseInt(postObj.zidx_more_search_acreage_to);
        if(searchFields.zidx_search_acreage_to==0){
            searchFields.zidx_search_acreage_to=1000000000;
        }
    }
    if (postObj.zidx_more_search_year_built_from != "") {
        searchFields.zidx_search_year_built_from = parseInt(postObj.zidx_more_search_year_built_from);
    }
    if (postObj.zidx_more_search_year_built_to != "") {
        searchFields.zidx_search_year_built_to = parseInt(postObj.zidx_more_search_year_built_to);
        if(searchFields.zidx_search_year_built_to==0){
            searchFields.zidx_search_year_built_to=10000;
        }
    }
    if (postObj.zidx_more_search_sqft_from != "") {
        searchFields.zidx_search_sqft_from = parseInt(postObj.zidx_more_search_sqft_from);
    }
    if (postObj.zidx_more_search_sqft_to != "") {
        searchFields.zidx_search_sqft_to = parseInt(postObj.zidx_more_search_sqft_to);
        if(searchFields.zidx_search_sqft_to==0){
            searchFields.zidx_search_sqft_to=1000000000;
        }
    }
    if(typeof postObj.zidx_search_show_listings != "undefined") {
        searchFields.zidx_search_show_listings=postObj.zidx_search_show_listings;
    }else{
        searchFields.zidx_search_show_listings="0";
    }
    limitTo32Values(base, "zidx_search_listing_id", searchFields, postObj);
    limitTo32Values(base, "zidx_search_address", searchFields, postObj);
    limitTo32Values(base, "zidx_search_city", searchFields, postObj);
    limitTo32Values(base, "zidx_search_subdivision", searchFields, postObj);
    limitTo32Values(base, "zidx_search_postal_code", searchFields, postObj);
    limitTo32Values(base, "zidx_search_remarks", searchFields, postObj);
    limitTo32Values(base, "zidx_search_pool", searchFields, postObj);
    limitTo32Values(base, "zidx_search_building", searchFields, postObj);
    limitTo32Values(base, "zidx_search_exclude_remarks", searchFields, postObj);
    limitTo32Values(base, "zidx_search_agent_name", searchFields, postObj);
    limitTo32Values(base, "zidx_search_office_name", searchFields, postObj);
    if (typeof postObj.zidx_search_listing_id != "undefined") {
        searchFields.zidx_search_listing_id = postObj.zidx_search_listing_id;
    }
    if (typeof postObj.zidx_search_listing_table_id != "undefined") {
        searchFields.zidx_search_listing_table_id = postObj.zidx_search_listing_table_id;
    }
    if (typeof postObj.zidx_nearlocation_address != "undefined") {
        searchFields.zidx_nearlocation_address = postObj.zidx_nearlocation_address;
    }
    if (typeof postObj.zidx_nearlocation_latitude != "undefined") {
        searchFields.zidx_nearlocation_latitude = parseFloat(postObj.zidx_nearlocation_latitude);
    }
    if (typeof postObj.zidx_nearlocation_longitude != "undefined") {
        searchFields.zidx_nearlocation_longitude = parseFloat(postObj.zidx_nearlocation_longitude);
    }
    if (typeof postObj.zidx_nearlocation_distanceInMiles != "undefined") {
        searchFields.zidx_nearlocation_distanceInMiles = parseFloat(postObj.zidx_nearlocation_distanceInMiles);
    }
    return searchFields;
}

export function limitTo32Values(base: ZIDXBase, fieldName: string, searchFields: ZIDXSearchFields, postObj: any) {
    if (typeof postObj[fieldName] == "undefined") {
        return;
    }
    postObj[fieldName] = postObj[fieldName].toLowerCase();
    let a = postObj[fieldName].split(",");
    if (a.length > 32) {
        peakAlert("There is a limit of 32 values.");
        let newValue = a.splice(0, 32).join(",");
        postObj[fieldName] = newValue;
        searchFields[fieldName] = newValue;
        $("#" + fieldName, base.zidx.peakidxShadowRoot).val(newValue);
    } else {
        searchFields[fieldName] = postObj[fieldName];
    }
}

export function checkForSearchFormChanges(base: ZIDXBase, forceSearch: boolean, resetListingData: boolean) {
    if (base.zidx.peakidxShadowRoot.getElementById("zidxSearchTopForm") == null) {
        return;
    }
    let postObjTemp = base.zidx.getFormDataByFormId("zidxSearchTopForm");
    let postObj = dedupeSearchForm(base, postObjTemp);
    base.updateSearchCounts(postObj);
    base.updateSearchFormLabels(postObj);

    let formChanged = false;
    if (forceSearch) {
        formChanged = true;
    } else {
        for (let i in postObj) {
            if (!postObj.hasOwnProperty(i)) {
                continue;
            }
            if (typeof base.lastPostObj[i] == "undefined" || base.lastPostObj[i] != postObj[i]) {
                if (base.zidx.options.debug) console.log(i + " changed from ", base.lastPostObj[i], "to", postObj[i]);
                formChanged = true;
            }
        }
    }
    if (formChanged) {
        $(".zidxSearchResultsContainer", base.zidx.peakidxShadowRoot).removeClass("zidxSearchResultsDisabledContainer");
        if($(".zidxSearchResultsDisabledContainer", base.zidx.peakidxShadowRoot).length>0) {
            $(".zidxLoadMoreContainer", base.zidx.peakidxShadowRoot).hide();
            $(".zidxSearchLayoutLinks", base.zidx.peakidxShadowRoot).hide();
        }else{
            $(".zidxSearchLayoutLinks", base.zidx.peakidxShadowRoot).show();
        }
        base.resetListingData = resetListingData;
        if (base.zidx.options.debug) console.log("this.checkForSearchFormChanges", postObj, "resetListingData:", resetListingData);
        base.updateSearchFormLabels(postObj);
        if (resetListingData) {
            clearTimeout(base.doSearchTimeout);
        }
        if(base.infiniteScroll) {
            base.infiniteScroll.loadedCount = base.infiniteScroll.loadPerPage;
        }
        base.doSearchTimeout = setTimeout(function () {
            doSearch(base, postObj, forceSearch);
        }, 100);
        base.lastPostObj = postObj;
    }
}
